import React, { useCallback, useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal, Select } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';


import { generateShareToken, getShareToken } from '../../../hooks/interoperability';
import { ShareToken, ShareTokenTtl } from '../../../types/responses/Interoperability';
import { CollapsibleSection } from '../../molecules/CollapsibleSection';

/**
 * Token de compartilhamento da conta, na tela principal do modulo (MV2-1706).
 *
 * E o endereco que a conta entrega ao parceiro para ser convidada. Fica visivel a
 * qualquer momento, e nao uma unica vez como o token de convite: quem tem este
 * token consegue convidar esta conta, e nada mais — aceitar continua sendo ato do
 * dono dela. Por isso ele e legivel no banco e pode ser reexibido.
 */

/** Sem moldura nem margem: quem desenha borda, fundo e espaco agora e a CollapsibleSection. */
const Card = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const Left = styled.div`
  flex: 1;
  min-width: 260px;
`;

const Hint = styled.div`
  color: #8a9099;
  font-size: 11px;
  margin-top: 2px;
  max-width: 560px;
  line-height: 1.5;
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const TokenBox = styled.code<{ stale: boolean }>`
  background: #f6f8fa;
  border: 1px solid ${({ stale }) => (stale ? '#eddfb8' : '#e3e6ea')};
  border-radius: 6px;
  padding: 7px 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: ${({ stale }) => (stale ? '#8a5300' : '#12263f')};
  word-break: break-all;
`;

const Button = styled.button<{ primary?: boolean }>`
  background: ${({ primary, theme }) => (primary ? theme.primary : '#fff')};
  color: ${({ primary }) => (primary ? '#fff' : '#4a5462')};
  border: 1px solid ${({ primary, theme }) => (primary ? theme.primary : '#d9dce1')};
  border-radius: 6px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 11.5px;
  white-space: nowrap;

  &:disabled {
    color: #a9b4c2;
    background: #fff;
    border-color: #e3e6ea;
    cursor: not-allowed;
  }
`;

const Copied = styled.span`
  color: #1e7a45;
  font-size: 11px;
`;

const Validity = styled.div<{ stale: boolean }>`
  color: ${({ stale }) => (stale ? '#b02525' : '#6b7280')};
  font-size: 11px;
  margin-top: 6px;
`;

const Renew = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  border-left: 1px solid #f0f2f4;
  padding-left: 16px;
`;

const TTL_OPTIONS: { value: ShareTokenTtl; label: string }[] = [
  { value: 'NEVER', label: 'does not expire' },
  { value: 'DAYS_7', label: '7 days' },
  { value: 'DAYS_30', label: '30 days' },
  { value: 'DAYS_90', label: '90 days' },
  { value: 'DAYS_180', label: '180 days' },
  { value: 'DAYS_365', label: '1 year' },
];

const formatDate = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const moment = new Date(value);

  return Number.isNaN(moment.getTime()) ? '' : moment.toLocaleDateString();
};

export type ShareTokenCardProps = {
  tenantId: string;
};

export const ShareTokenCard: React.FC<ShareTokenCardProps> = ({ tenantId }) => {
  const [token, setToken] = useState<ShareToken | null>(null);
  const [ttl, setTtl] = useState<ShareTokenTtl>('NEVER');

  /**
   * `loading` e so a PRIMEIRA leitura, e por isso nunca volta a ser verdadeiro.
   *
   * O card devolve null enquanto ela corre, para nao piscar "esta conta nao tem token"
   * antes da resposta. Se o botao de tentar de novo voltasse a ligar este estado, o card
   * inteiro desmontaria — e a CollapsibleSection remontaria fechada, tirando da tela a
   * area que a pessoa acabou de abrir.
   */
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setReloading(true);
      setFailed(false);
      setToken(await getShareToken(tenantId));
    } catch {
      setFailed(true);
    } finally {
      setReloading(false);
      // A primeira leitura terminou, com erro ou sem: dai em diante o card fica na tela.
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async () => {
    try {
      setBusy(true);
      setCopied(false);
      setToken(await generateShareToken(tenantId, ttl));
      // Limpa o erro da leitura anterior: sem isto, o bloco do token continuava atras do
      // `!failed` e o valor novo — que existe em claro uma vez so — nao era exibido nem
      // dava para copiar, logo depois de ter invalidado o antigo.
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Renovar invalida o token anterior, e a confirmacao diz o efeito em vez de
   * perguntar "tem certeza?": quem tinha o antigo perde a capacidade de convidar
   * esta conta.
   *
   * Modal.confirm do antd, como o resto do dashboard — era o unico lugar do repo
   * usando window.confirm, que ignora o tema e nao da para traduzir os botoes.
   *
   * Gerar o primeiro token nao pergunta nada: nao ha o que invalidar.
   *
   * "Nao ha token" e "nao sabemos" sao estados diferentes, e era so isso que faltava
   * distinguir: uma leitura que falha tambem deixa `token` nulo, e a condicao antiga lia
   * isso como conta sem token — o botao dizia "Gerar token" e girava o token sem
   * perguntar nada, invalidando o que os parceiros tinham em maos por causa de um GET que
   * falhou. Na duvida, pergunta.
   */
  const generate = () => {
    if (!token && !failed) {
      run();
      return;
    }

    Modal.confirm({
      title: T('Renew the account token?'),
      icon: <ExclamationCircleOutlined />,
      content: failed
        ? T(
            'The current token could not be read. Generating a new one invalidates whatever token exists, and partners holding it will no longer be able to invite you.',
          )
        : T(
            'Partners who already have the current token will no longer be able to invite you. Send them the new one.',
          ),
      okText: T('Renew'),
      cancelText: T('Cancel'),
      okButtonProps: { danger: true },
      onOk: run,
    });
  };

  const copy = async () => {
    if (!token) {
      return;
    }

    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
    } catch {
      // Sem permissao de area de transferencia o token segue selecionavel na tela.
      setCopied(false);
    }
  };

  if (loading) {
    return null;
  }

  /**
   * O resumo do cabecalho fechado.
   *
   * Diz o estado sem obrigar a abrir — e o token em si nao entra aqui: ele fica atras de um
   * clique, em vez de exposto no alto da tela para qualquer um que passe pela mesa.
   */
  const summary = (): string => {
    if (failed) {
      return TF('Could not load the token. Try again.');
    }

    if (!token) {
      return TF('This account has no token yet.');
    }

    if (token.expired) {
      return `${TF('Expired on')} ${formatDate(token.expires_at)}`;
    }

    return token.expires_at
      ? `${TF('Valid until')} ${formatDate(token.expires_at)}`
      : TF('Does not expire');
  };

  return (
    <CollapsibleSection title={T('Your account token')} summary={summary()}>
      <Card>
      <Left>
        <Hint>
          {T(
            'Hand this token to a partner so they can invite your account. Whoever has it can only invite you — accepting is still your decision.',
          )}
        </Hint>

        {failed && (
          <TokenRow>
            <Validity stale>{T('Could not load the token. Try again.')}</Validity>
            <Button type="button" disabled={busy || reloading} onClick={load}>
              {T('Try again')}
            </Button>
          </TokenRow>
        )}

        {!failed && !token && (
          <TokenRow>
            <Validity stale={false}>{T('This account has no token yet.')}</Validity>
          </TokenRow>
        )}

        {!failed && token && (
          <>
            <TokenRow>
              <TokenBox stale={token.expired}>{token.token}</TokenBox>
              <Button type="button" onClick={copy}>
                {T('Copy')}
              </Button>
              {copied && <Copied>{T('Copied')} ✓</Copied>}
            </TokenRow>

            <Validity stale={token.expired}>
              {token.expired
                ? `${T('Expired on')} ${formatDate(token.expires_at)} — ${T('renew to share again')}`
                : token.expires_at
                  ? `${T('Valid until')} ${formatDate(token.expires_at)}`
                  : T('Does not expire')}
              {token.renewed_at && ` · ${T('renewed on')} ${formatDate(token.renewed_at)}`}
            </Validity>
          </>
        )}
      </Left>

      <Renew>
        <Select
          value={ttl}
          disabled={busy}
          style={{ minWidth: 150 }}
          onChange={(value: ShareTokenTtl) => setTtl(value)}
        >
          {TTL_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {T(option.label)}
            </Select.Option>
          ))}
        </Select>

        <Button type="button" primary disabled={busy} onClick={generate}>
          {/* `failed` cai em Renovar de proposito: o rotulo tem de sugerir o efeito
              destrutivo quando nao sabemos se ja existe token. */}
          {busy ? T('Preparing...') : token || failed ? T('Renew') : T('Generate token')}
        </Button>

      </Renew>
      </Card>
    </CollapsibleSection>
  );
};
